import React from 'react';
import type { useRouter } from 'next/navigation';
import type { Translate } from '@locales/client';
import { getGamePlayPath } from '@/shared/libs/get-game-play-path';
import { getProviderGamesPath } from '@/shared/libs/get-provider-games-path';
import { useLocaleFormat } from '@/shared/hooks/useLocaleFormat';

import { HeadingBoldH5, BodyTextRegularB } from '@/shared/ui/typography2';

import { GamesWrapper, ProviderImage } from '../styles';
import { ProviderBox } from './styles';

import { Condition } from '@/shared/ui/condition/condition';

import { ConditionalWrapperGame } from '@/entities/game/ui/conditional-wrapper-game';
import { useGamesByIds } from '@/entities/game/model/api';
import { useProviderById } from '@/entities/provider/model/use-provider-by-id';
import type { GameIdInput } from '@/entities/game/model/games-by-ids-logic';

interface BonusGamesAndProvidersProps {
  bonus?: {
    provider?: string | null;
    games?: readonly GameIdInput[] | null;
  } | null;
  tCommon: Translate;
  t: Translate;
  router: ReturnType<typeof useRouter>;
  locale: string;
}

export const BonusGamesAndProviders = ({
  bonus,
  tCommon,
  t,
  router,
  locale,
}: BonusGamesAndProvidersProps) => {
  const formatUrl = useLocaleFormat();

  const currentProvider = useProviderById(bonus?.provider);

  const { games: bonusGames } = useGamesByIds(bonus?.games);
  const isSingleGame = bonus?.games?.length === 1;

  return (
    <>
      <Condition match={!Boolean(currentProvider) && !Boolean(bonus?.games)}>
        <BodyTextRegularB> {t('card.allowed_all')}</BodyTextRegularB>
      </Condition>

      <Condition match={!!currentProvider}>
        <HeadingBoldH5> {tCommon('filter.label.provider')}</HeadingBoldH5>
        <ProviderBox
          onClick={() =>
            router.push(
              `/${locale}${getProviderGamesPath(currentProvider?.id)}`
            )
          }
        >
          <ProviderImage
            style={{ objectFit: 'contain' }}
            src={currentProvider?.imageUrl ?? ''}
            width={66}
            height={40}
            alt={currentProvider?.title ?? ''}
          />
        </ProviderBox>
      </Condition>

      <Condition match={!!bonus?.games}>
        <HeadingBoldH5>
          {isSingleGame ? t('card.modal_game') : t('card.modal_games')}
        </HeadingBoldH5>

        <GamesWrapper $centered={isSingleGame}>
          {bonusGames.map((game) => {
            return (
              <ConditionalWrapperGame
                key={game.id}
                game={game}
                handleClickGame={() =>
                  router.push(formatUrl(getGamePlayPath(game)))
                }
                hasScroll={false}
              />
            );
          })}
        </GamesWrapper>
      </Condition>
    </>
  );
};
